// src/Dashboard/pages/user/Galleries/components/AlbumDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaEye,
  FaEyeSlash,
  FaImages,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  addGallery,
  updateGallery,
} from "../../../../../services/gallerieServices";

import {
  normalizePlainText,
  normalizeRichText,
  stripHtml,
} from "../utils/galleriesUtils";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "clean",
];

const emptyForm = {
  titre: "",
  description: "",
  is_active: true,
};

export default function AlbumDrawer({
  isOpen,
  mode = "create",
  gallery,
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit" && !!gallery?.id;
  const firstFieldRef = useRef(null);

  const initialForm = useMemo(() => {
    if (!isEdit) return emptyForm;

    return {
      titre: gallery?.titre || "",
      description: gallery?.description || "",
      is_active: !!gallery?.is_active,
    };
  }, [isEdit, gallery]);

  const [formData, setFormData] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(initialForm);
    setSaving(false);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const timer = window.setTimeout(() => {
      firstFieldRef.current?.focus();
    }, 80);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        safeClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialForm]);

  if (!isOpen) return null;

  const isDirty =
    formData.titre !== initialForm.titre ||
    formData.description !== initialForm.description ||
    formData.is_active !== initialForm.is_active;

  const descChars = stripHtml(formData.description).length;

  const safeClose = () => {
    if (saving) return;

    if (
      !isDirty ||
      window.confirm("Des informations ne sont pas enregistrées. Quitter ?")
    ) {
      onClose?.();
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const titre = normalizePlainText(formData.titre);

    if (!titre) {
      toast.warn("Le titre de l’album est obligatoire.");
      firstFieldRef.current?.focus();
      return;
    }

    try {
      setSaving(true);

      const payload = {
        titre,
        description: normalizeRichText(formData.description) || null,
        is_active: !!formData.is_active,
      };

      if (isEdit) {
        await updateGallery(gallery.id, payload);
        toast.success("Album mis à jour.");
      } else {
        await addGallery(payload);
        toast.success("Album créé.");
      }

      await onSaved?.();
    } catch (err) {
      console.error("Erreur sauvegarde album :", err);
      toast.error("Erreur lors de l’enregistrement de l’album.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={safeClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              <FaImages />
              Galerie photos
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              {isEdit ? "Modifier l’album" : "Créer un album"}
            </h2>
          </div>

          <button
            type="button"
            onClick={safeClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="space-y-5">
              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Titre de l’album *
                </label>

                <input
                  ref={firstFieldRef}
                  type="text"
                  name="titre"
                  maxLength={120}
                  value={formData.titre}
                  onChange={handleChange}
                  placeholder="Ex. Tournoi régional — Janvier 2026"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {formData.titre.length}/120 caractères
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Description
                </label>

                <div className="overflow-hidden rounded-2xl border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950">
                  <ReactQuill
                    value={formData.description}
                    onChange={(value) =>
                      setFormData((previous) => ({
                        ...previous,
                        description: value,
                      }))
                    }
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Décrivez l’événement, le lieu, les résultats, les remerciements..."
                  />
                </div>

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {descChars} caractère{descChars > 1 ? "s" : ""} en texte brut
                </p>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <label className="mb-3 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Visibilité
                </label>

                <button
                  type="button"
                  onClick={() =>
                    setFormData((previous) => ({
                      ...previous,
                      is_active: !previous.is_active,
                    }))
                  }
                  className={`inline-flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold transition ${
                    formData.is_active
                      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                      : "bg-slate-200 text-slate-700 ring-1 ring-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700"
                  }`}
                >
                  {formData.is_active ? <FaEye /> : <FaEyeSlash />}
                  {formData.is_active
                    ? "Album visible"
                    : "Album masqué"}
                </button>

                <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Un album masqué reste accessible aux administrateurs et
                  enseignants, mais n’est pas affiché aux utilisateurs.
                </p>
              </div>
            </div>
          </div>

          <footer className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95">
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={safeClose}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={saving || !isDirty || !normalizePlainText(formData.titre)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving
                  ? "Enregistrement..."
                  : isEdit
                  ? "Enregistrer"
                  : "Créer l’album"}
              </button>
            </div>
          </footer>
        </form>
      </aside>
    </div>
  );
}